/*
 * The bell and its panel (docs/notifications.md). Unread first, then by day.
 * Each row is one link to what it's about (Ryan, 2026-10-22: a row was
 * "cluttered, looks very busy"): the title on one line, the fact first; one
 * dim line under it with the time; and one mark, the unread dot, in its
 * status tone, fixed whatever the brand: urgent is critical (red), action a
 * warning (amber), info blue. A read row has no mark. Words for the tone are
 * there for screen readers. A row names the site, server or domain only when
 * its title doesn't already. Buttons beyond the link ("Restart now") sit
 * under the line, quiet.
 *
 * NotificationsPanel only draws what it's given, so the components page can
 * show it with sample rows. NotificationsBell is the real one: hidden only
 * when the API has no inbox (GET /me/inbox).
 */
import { forwardRef, useCallback, useState, type ButtonHTMLAttributes } from "react";
import { Popover as P } from "radix-ui";
import { Link, useNavigate } from "react-router";
import { useToast } from "../ui/Toast";
import { useMutation } from "@tanstack/react-query";
import { api } from "../api/client";
import { watchOperation } from "../api/live";
import { useMarkNotificationsRead, useNotifications, useNotificationsLive, type AppNotification, type NotificationAction, type NotificationSeverity } from "../api/notifications";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { FormError } from "../ui/Field";
import { cx } from "../lib/cx";
import { SEGMENT_OFF, SEGMENT_ON } from "../ui/segment";
import { dayLabel, plural, relativeTime } from "../lib/format";
import { IconBell } from "../ui/icons";
import { useOrg, useOrgPath } from "./org";

/** What it's about, when the title doesn't already say: "Acme · Live". Another org's rows carry that org's name. */
function aboutWords(n: AppNotification, currentOrgId?: string): string | null {
  const title = n.title.toLowerCase();
  const parts = [n.site?.name, n.environment?.name, n.server?.name, n.domain?.name].filter((p): p is string => !!p && !title.includes(p.toLowerCase()));
  if (n.org && currentOrgId && n.org.id !== currentOrgId && !title.includes(n.org.name.toLowerCase())) parts.unshift(n.org.name);
  return parts.length ? parts.join(" · ") : null;
}

/** The unread dot's tone, and its words. */
const TONE: Record<NotificationSeverity, { dot: string; words: string }> = {
  urgent: { dot: "bg-bad-fill", words: "urgent" },
  action: { dot: "bg-warn-fill", words: "needs you" },
  info: { dot: "bg-info-fill", words: "for your information" },
};

function Row({ n, now, onOpen, onAct, currentOrgId }: { n: AppNotification; now: number; onOpen: (n: AppNotification) => void; onAct?: (n: AppNotification, a: NotificationAction) => void; currentOrgId?: string }) {
  const unread = !n.readAt;
  const tone = TONE[n.severity] ?? TONE.info;
  const line = [aboutWords(n, currentOrgId), n.detail].filter(Boolean).join(" · ");
  // Buttons that do what the row's link doesn't ("Restart now"); a link to the row's own page ("Move it") is the row itself.
  const extra = onAct ? (n.actions ?? []).filter((a) => a.api || (a.link && a.link !== n.link)) : [];
  const title = (
    <>
      {unread ? <span className="sr-only">Unread, {tone.words}: </span> : null}
      {n.title}
    </>
  );
  return (
    // The dot's column is there on every row, so read and unread titles line up. A row with a link is the link (its ::after covers the row).
    <li className={cx("relative grid grid-cols-[6px_minmax(0,1fr)] gap-x-3 px-4 py-3", n.link && "hover:bg-hover has-[a[data-row]:focus-visible]:bg-hover has-[a[data-row]:focus-visible]:shadow-[inset_0_0_0_2px_var(--color-brand-500)]")}>
      <span aria-hidden="true" className={cx("mt-[7px] h-1.5 w-1.5 rounded-full", unread && tone.dot)} />
      <div className="min-w-0">
        <p className="flex min-w-0 items-baseline gap-1 text-[14px] leading-5 font-medium text-fg">
          {n.link ? (
            <Link data-row="" to={n.link} onClick={() => onOpen(n)} title={n.title} className="min-w-0 truncate outline-none after:absolute after:inset-0 after:content-['']">
              {title}
            </Link>
          ) : (
            <span className="min-w-0 truncate" title={n.title}>
              {title}
            </span>
          )}
          {n.repeats > 1 ? <span className="shrink-0 font-normal text-dim">({plural(n.repeats, "time")})</span> : null}
        </p>
        <div className="mt-0.5 flex items-baseline gap-3 text-[12.5px] leading-[18px]">
          <p className="min-w-0 flex-1 truncate text-dim" title={line || undefined}>
            {line}
          </p>
          <time dateTime={n.updatedAt} className="shrink-0 text-[12px] text-faint tabular-nums">
            {relativeTime(n.updatedAt, now)}
          </time>
        </div>
        {extra.length ? (
          <div className="relative z-[1] mt-2 flex flex-wrap gap-2">
            {extra.map((a) => (
              <Button key={a.label} size="sm" variant="secondary" className="h-7! px-2.5! text-[12.5px]!" onClick={() => onAct!(n, a)}>
                {a.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function Group({ title, items, now, onOpen, onAct, currentOrgId }: { title: string; items: AppNotification[]; now: number; onOpen: (n: AppNotification) => void; onAct?: (n: AppNotification, a: NotificationAction) => void; currentOrgId?: string }) {
  if (!items.length) return null;
  return (
    <section className="border-t border-line-soft first:border-t-0">
      <h3 className="sticky top-0 z-[1] bg-raised px-4 pt-2.5 pb-1 text-[12px] font-medium text-dim">{title}</h3>
      <ul className="divide-y divide-line-soft">
        {items.map((n) => (
          <Row key={n.id} n={n} now={now} onOpen={onOpen} onAct={onAct} currentOrgId={currentOrgId} />
        ))}
      </ul>
    </section>
  );
}

/** Day groups, newest first: "Today", "Yesterday", "Oct 18". */
function byDay(items: AppNotification[], now: number): [string, AppNotification[]][] {
  const out = new Map<string, AppNotification[]>();
  for (const n of [...items].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))) {
    const k = dayLabel(n.updatedAt, new Date(now));
    out.set(k, [...(out.get(k) ?? []), n]);
  }
  return [...out.entries()];
}

export function NotificationsPanel({
  items,
  unread,
  filter,
  onFilter,
  onMarkAllRead,
  onOpen,
  onAct,
  settingsHref,
  activityHref,
  now = Date.now(),
  marking,
  currentOrgId,
}: {
  items: AppNotification[];
  unread: number;
  filter: "unread" | "all";
  onFilter: (f: "unread" | "all") => void;
  onMarkAllRead: () => void;
  onOpen: (n: AppNotification) => void;
  /** A row's other actions; an API call is confirmed first. */
  onAct?: (n: AppNotification, a: NotificationAction) => void;
  settingsHref: string;
  activityHref: string;
  now?: number;
  marking?: boolean;
  /** The org being looked at; rows from another org name theirs. */
  currentOrgId?: string;
}) {
  const unreadItems = items.filter((n) => !n.readAt).sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  const readItems = items.filter((n) => n.readAt);
  const group = (title: string, list: AppNotification[]) => <Group key={title} title={title} items={list} now={now} onOpen={onOpen} onAct={onAct} currentOrgId={currentOrgId} />;
  return (
    <div className="flex max-h-[min(640px,calc(100dvh-88px))] w-[min(400px,calc(100vw-24px))] flex-col overflow-hidden rounded-[14px] bg-raised shadow-[var(--hx-shadow-pop)]">
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <h2 className="text-[15px] font-semibold text-fg">Notifications</h2>
        <div role="group" aria-label="Show" className="flex items-center gap-0.5 text-[12.5px]">
          {(["unread", "all"] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => onFilter(f)}
              className={cx("h-6 rounded-[6px] px-2 focus-visible:outline-2 focus-visible:outline-brand-500", filter === f ? cx("font-medium", SEGMENT_ON) : SEGMENT_OFF)}
            >
              {f === "unread" ? `Unread${unread ? ` ${unread}` : ""}` : "All"}
            </button>
          ))}
        </div>
        <button type="button" onClick={onMarkAllRead} disabled={!unread || marking} className="ml-auto shrink-0 text-[12.5px] font-medium text-link hover:underline disabled:cursor-default disabled:text-faint disabled:no-underline">
          Mark all read
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {filter === "unread" ? (
          unreadItems.length ? (
            byDay(unreadItems, now).map(([day, list]) => group(day, list))
          ) : (
            <div className="px-6 py-10 text-center">
              <p className="text-[14px] font-medium text-fg">Nothing unread</p>
              <p className="mt-1 text-[13px] text-dim">Older ones are under All.</p>
            </div>
          )
        ) : items.length ? (
          <>
            {group("Unread", unreadItems)}
            {byDay(readItems, now).map(([day, list]) => group(day, list))}
          </>
        ) : (
          <p className="px-6 py-10 text-center text-[13px] text-dim">No notifications in the last 90 days.</p>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-[12.5px]">
        <Link to={settingsHref} className="text-dim hover:text-fg hover:underline">
          Notification settings
        </Link>
        <Link to={activityHref} className="text-dim hover:text-fg hover:underline">
          Activity
        </Link>
      </div>
    </div>
  );
}

/** The bell with its unread count, in the worst unread severity's tone: red when something urgent is, amber when something needs you, else blue. */
export const BellButton = forwardRef<HTMLButtonElement, { unread: number; urgent: number; action?: number } & ButtonHTMLAttributes<HTMLButtonElement>>(function BellButton({ unread, urgent, action = 0, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={unread ? `Notifications, ${unread} unread${urgent ? `, ${urgent} urgent` : action ? `, ${action} need you` : ""}` : "Notifications"}
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-[9px] text-body transition-colors hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500 data-[state=open]:bg-hover data-[state=open]:text-fg"
      {...rest}
    >
      <IconBell size={19} />
      {unread ? (
        <span
          aria-hidden="true"
          className={cx(
            "absolute top-0.5 right-0.5 grid h-4 min-w-4 place-items-center rounded-full px-[3px] text-[10px] leading-none font-semibold tabular-nums shadow-[0_0_0_2px_var(--hx-canvas)]",
            // The status fills (styles.css), with the text that reads on each: dark on amber, and on every fill in the dark theme.
            urgent ? "bg-bad-fill text-white dark:text-canvas" : action ? "bg-warn-fill text-[#2b1d04] dark:text-canvas" : "bg-info-fill text-white dark:text-canvas",
          )}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
    </button>
  );
});

/** The real bell: hidden only when the API has no inbox. Live: new ones arrive without a reload, and an urgent one also toasts. */
export function NotificationsBell() {
  const q = useNotifications();
  const mark = useMarkNotificationsRead();
  const path = useOrgPath();
  const org = useOrg();
  const toast = useToast();
  const navigate = useNavigate();
  const onUrgent = useCallback(
    (n: AppNotification) =>
      toast({
        tone: "error",
        title: n.title,
        description: n.detail ?? undefined,
        action: n.link ? { label: n.action ?? "Open", onClick: () => (mark.mutate({ ids: [n.id] }), navigate(n.link!)) } : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, navigate],
  );
  useNotificationsLive(onUrgent);
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<{ n: AppNotification; a: NotificationAction } | null>(null);
  const act = useMutation({
    mutationFn: ({ a }: { n: AppNotification; a: NotificationAction }) => {
      // The API's own paths start with /api, which the client adds.
      const p = a.api!.path.replace(/^\/api(?=\/)/, "");
      return a.api!.method === "PATCH" ? api.patch<{ operationId?: string | null }>(p, a.api!.body ?? {}) : api.post<{ operationId?: string | null }>(p, a.api!.body ?? {});
    },
    onSuccess: (res, { n, a }) => {
      // Its org is the notification's, which may not be the one open.
      if (res?.operationId) watchOperation(res.operationId, undefined, n.org ? { id: n.org.id, slug: n.org.slug } : undefined);
      if (!n.readAt) mark.mutate({ ids: [n.id] });
      setAsking(null);
      toast({ tone: "info", title: confirmLabel(n, a), description: "Started. The server's page follows it." });
    },
  });
  const [filter, setFilter] = useState<"unread" | "all">("unread");
  const data = q.data;
  if (!data) return null;
  return (
    <P.Root open={open} onOpenChange={setOpen}>
      <P.Trigger asChild>
        <BellButton unread={data.unread} urgent={data.urgentUnread} action={data.notifications.filter((n) => !n.readAt && n.severity === "action").length} />
      </P.Trigger>
      <P.Portal>
        <P.Content align="end" sideOffset={8} collisionPadding={12} className="hx-pop z-[60] outline-none">
          <NotificationsPanel
            items={data.notifications}
            unread={data.unread}
            filter={filter}
            onFilter={setFilter}
            marking={mark.isPending}
            onMarkAllRead={() => mark.mutate({ all: true })}
            onOpen={(n) => {
              if (!n.readAt) mark.mutate({ ids: [n.id] });
              setOpen(false);
            }}
            onAct={(n, a) => {
              setOpen(false);
              if (a.api) {
                act.reset();
                setAsking({ n, a });
              } else if (a.link) {
                if (!n.readAt) mark.mutate({ ids: [n.id] });
                navigate(a.link);
              }
            }}
            settingsHref={path("settings/notifications")}
            activityHref={path("activity")}
            currentOrgId={org.id}
          />
        </P.Content>
      </P.Portal>
      <Dialog
        open={!!asking}
        onOpenChange={(v) => !v && setAsking(null)}
        size="sm"
        title={asking ? `${confirmLabel(asking.n, asking.a)}?` : ""}
        footer={
          asking ? (
            <>
              <Button variant="secondary" onClick={() => setAsking(null)}>
                Cancel
              </Button>
              <Button variant="primary" loading={act.isPending} loadingText="Starting" onClick={() => act.mutate(asking)}>
                {confirmLabel(asking.n, asking.a)}
              </Button>
            </>
          ) : null
        }
      >
        {asking ? (
          <div className="space-y-2 text-[14px] leading-relaxed text-body">
            <p>{asking.n.title.replace(/\.$/, "")}.</p>
            {asking.a.confirm || asking.n.detail ? <p className="text-dim">{asking.a.confirm ?? asking.n.detail}</p> : null}
            {act.error ? <FormError>{(act.error as Error).message}</FormError> : null}
          </div>
        ) : null}
      </Dialog>
    </P.Root>
  );
}

/** What the confirm button says: "Restart test-1 now" for a restart, else the action's own words. */
function confirmLabel(n: AppNotification, a: NotificationAction): string {
  return n.server && /^restart now$/i.test(a.label) ? `Restart ${n.server.name} now` : a.label;
}
